import { useEffect, useMemo, useState } from 'react';
import { RefreshCw, X, Download, CheckCircle2, XCircle, MinusCircle } from 'lucide-react';
import { toast } from 'sonner';
import { apiService } from '@/services/api';
import { ItemVerificacao, ITENS_VERIFICACAO_PADRAO } from '@/types/lista-verificacao';
import { useI18n } from '@/contexts/i18n-hook';
import { descricaoItemVerificacao } from './constants/lista-verificacao-i18n';

interface AnaliseVerificacoesModalProps {
  aberto: boolean;
  apreciacaoIds: string[];
  onClose: () => void;
}

interface LinhaMatriz {
  apreciacaoId: string;
  numero: string;
  titulo: string;
  nomeMaquina: string;
  setor: string;
  identificacao: string;
  itens: Record<string, ItemVerificacao | undefined>;
}

function normalizarLista(dados: any): ItemVerificacao[] {
  if (!dados) {
    return ITENS_VERIFICACAO_PADRAO.map((i) => ({ ...i, possui: null, observacao: '' }));
  }
  if (Array.isArray(dados)) return dados as ItemVerificacao[];
  if (dados.itens && Array.isArray(dados.itens)) return dados.itens as ItemVerificacao[];

  // Objeto chaveado por código (C.4.1, C.4.2, ...) — mesma lógica do componente de edição.
  const valoresSalvos = Object.values(dados) as any[];
  return ITENS_VERIFICACAO_PADRAO.map((itemPadrao) => {
    const porDescricao = valoresSalvos.find(
      (v) => v && typeof v === 'object' && v.descricao === itemPadrao.descricao,
    );
    if (porDescricao) {
      return {
        codigo: itemPadrao.codigo,
        descricao: itemPadrao.descricao,
        possui: porDescricao.possui ?? null,
        observacao: porDescricao.observacao || '',
      };
    }
    const porCodigo = (dados as any)[itemPadrao.codigo];
    if (porCodigo && typeof porCodigo === 'object') {
      return {
        codigo: itemPadrao.codigo,
        descricao: itemPadrao.descricao,
        possui: porCodigo.possui ?? null,
        observacao: porCodigo.observacao || '',
      };
    }
    return { ...itemPadrao };
  });
}

function rotuloPossui(t: (chave: string) => string, possui: boolean | null | undefined): string {
  if (possui === true) return t('apreciacaoTabelas.analise.rotuloPossui.sim');
  if (possui === false) return t('apreciacaoTabelas.analise.rotuloPossui.nao');
  return t('apreciacaoTabelas.analise.rotuloPossui.semInfo');
}

export function AnaliseVerificacoesModal({
  aberto,
  apreciacaoIds,
  onClose,
}: AnaliseVerificacoesModalProps) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [linhas, setLinhas] = useState<LinhaMatriz[]>([]);
  const [progresso, setProgresso] = useState(0);

  useEffect(() => {
    if (!aberto || apreciacaoIds.length === 0) return;
    let cancelado = false;

    const carregar = async () => {
      setLoading(true);
      setProgresso(0);
      setLinhas([]);
      const resultados: LinhaMatriz[] = [];
      for (let i = 0; i < apreciacaoIds.length; i++) {
        const id = apreciacaoIds[i];
        try {
          const res = await apiService.buscarApreciacao(id);
          if (res.success && res.data) {
            const d: any = res.data;
            const itens = normalizarLista(d.listaVerificacaoAdministrativa);
            const itensPorCodigo: Record<string, ItemVerificacao> = {};
            itens.forEach((it) => {
              itensPorCodigo[it.codigo] = it;
            });
            resultados.push({
              apreciacaoId: id,
              numero: d.numero || '',
              titulo: d.titulo || '',
              nomeMaquina:
                d.nomeMaquina ||
                d.maquina?.modelo ||
                d.identificacaoMaquina ||
                d.titulo ||
                '—',
              setor: d.setorMaquina || d.maquina?.setor || '—',
              identificacao: d.identificacaoMaquina || d.maquina?.serie || '',
              itens: itensPorCodigo,
            });
          }
        } catch (e) {
          console.error('Erro ao buscar apreciação', id, e);
        }
        if (cancelado) return;
        setProgresso(i + 1);
      }
      if (!cancelado) {
        setLinhas(resultados);
        setLoading(false);
      }
    };

    carregar();
    return () => {
      cancelado = true;
    };
  }, [aberto, apreciacaoIds]);

  const colunas = useMemo(() => {
    const codigos = new Set<string>(ITENS_VERIFICACAO_PADRAO.map((i) => i.codigo));
    linhas.forEach((l) => Object.keys(l.itens).forEach((c) => codigos.add(c)));
    const descricoes: Record<string, string> = {};
    ITENS_VERIFICACAO_PADRAO.forEach((i) => {
      descricoes[i.codigo] = i.descricao;
    });
    linhas.forEach((l) =>
      Object.values(l.itens).forEach((it) => {
        if (it && !descricoes[it.codigo]) descricoes[it.codigo] = it.descricao;
      }),
    );
    return Array.from(codigos)
      .sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true }))
      .map((codigo) => ({ codigo, descricao: descricoes[codigo] || codigo }));
  }, [linhas]);

  const estatisticas = useMemo(() => {
    const stats: Record<string, { possui: number; naoPossui: number; semInfo: number }> = {};
    colunas.forEach((c) => {
      stats[c.codigo] = { possui: 0, naoPossui: 0, semInfo: 0 };
    });
    linhas.forEach((l) => {
      colunas.forEach((c) => {
        const it = l.itens[c.codigo];
        if (it?.possui === true) stats[c.codigo].possui++;
        else if (it?.possui === false) stats[c.codigo].naoPossui++;
        else stats[c.codigo].semInfo++;
      });
    });
    return stats;
  }, [colunas, linhas]);

  const exportarExcel = async () => {
    try {
      const XLSX = await import('xlsx');
      const { saveAs } = await import('file-saver');

      const colApreciacao = t('apreciacaoTabelas.analise.excel.apreciacao');
      const colMaquina = t('apreciacaoTabelas.analise.excel.maquina');
      const colSetor = t('apreciacaoTabelas.analise.excel.setor');
      const colIdentificacao = t('apreciacaoTabelas.analise.excel.identificacao');
      const colObs = (codigo: string) => `${codigo} — ${t('apreciacaoTabelas.analise.excel.obs')}`;
      const colItem = (c: { codigo: string; descricao: string }) =>
        `${c.codigo} — ${descricaoItemVerificacao(t, c.codigo, c.descricao)}`;

      const cabecalho: any = {
        [colApreciacao]: '',
        [colMaquina]: '',
        [colSetor]: '',
        [colIdentificacao]: '',
      };
      colunas.forEach((c) => {
        cabecalho[colItem(c)] = '';
        cabecalho[colObs(c.codigo)] = '';
      });

      const linhasExcel = linhas.map((l) => {
        const row: any = {
          [colApreciacao]: l.numero || l.titulo,
          [colMaquina]: l.nomeMaquina,
          [colSetor]: l.setor,
          [colIdentificacao]: l.identificacao,
        };
        colunas.forEach((c) => {
          const it = l.itens[c.codigo];
          row[colItem(c)] = rotuloPossui(t, it?.possui);
          row[colObs(c.codigo)] = it?.observacao || '';
        });
        return row;
      });

      // Linha de totais ao final
      const totais: any = {
        [colApreciacao]: t('apreciacaoTabelas.analise.excel.totais'),
        [colMaquina]: '',
        [colSetor]: '',
        [colIdentificacao]: '',
      };
      colunas.forEach((c) => {
        const s = estatisticas[c.codigo];
        totais[colItem(c)] = t('apreciacaoTabelas.analise.excel.resumoItem', {
          possui: s.possui,
          nao: s.naoPossui,
          semInfo: s.semInfo,
        });
        totais[colObs(c.codigo)] = '';
      });
      linhasExcel.push(totais);

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(linhasExcel, { header: Object.keys(cabecalho) });
      XLSX.utils.book_append_sheet(wb, ws, t('apreciacaoTabelas.analise.excel.aba'));
      const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const data = new Date().toISOString().slice(0, 10);
      saveAs(blob, `analise-verificacao-administrativa-${data}.xlsx`);
      toast.success(t('apreciacaoTabelas.analise.toast.gerada'));
    } catch (e) {
      console.error(e);
      toast.error(t('apreciacaoTabelas.analise.toast.erro'));
    }
  };

  if (!aberto) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
      <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-xl w-full max-w-[95vw] max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
          <div>
            <h3 className="text-lg font-bold text-schmersal-blue dark:text-white">
              {t('apreciacaoTabelas.analise.titulo')}
            </h3>
            <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
              {t('apreciacaoTabelas.analise.selecionadas', { qtd: apreciacaoIds.length })}
              {loading &&
                t('apreciacaoTabelas.analise.progresso', {
                  feitos: progresso,
                  total: apreciacaoIds.length,
                })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportarExcel}
              disabled={loading || linhas.length === 0}
              className="inline-flex items-center px-3 py-1.5 text-sm font-semibold text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
            >
              <Download className="h-4 w-4 mr-1.5" />
              {t('apreciacaoTabelas.analise.acao.exportar')}
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-md text-schmersal-gray-medium hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark"
              aria-label={t('comum.acao.fechar')}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-4">
          {loading && linhas.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="h-6 w-6 animate-spin text-schmersal-blue" />
              <span className="ml-2 text-sm text-schmersal-gray-dark dark:text-white">
                {t('apreciacaoTabelas.analise.carregando')}
              </span>
            </div>
          ) : linhas.length === 0 ? (
            <p className="text-sm text-schmersal-gray-medium text-center py-12">
              {t('apreciacaoTabelas.analise.vazio')}
            </p>
          ) : (
            <div className="overflow-auto border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-md">
              <table className="min-w-full text-xs border-collapse">
                <thead className="bg-schmersal-blue text-white sticky top-0 z-10">
                  <tr>
                    <th className="px-2 py-2 text-left font-bold border border-schmersal-blue-dark sticky left-0 bg-schmersal-blue z-20">
                      {t('apreciacaoTabelas.analise.coluna.maquina')}
                    </th>
                    <th className="px-2 py-2 text-left font-bold border border-schmersal-blue-dark">
                      {t('apreciacaoTabelas.analise.coluna.setor')}
                    </th>
                    <th className="px-2 py-2 text-left font-bold border border-schmersal-blue-dark">
                      {t('apreciacaoTabelas.analise.coluna.apreciacao')}
                    </th>
                    {colunas.map((c) => (
                      <th
                        key={c.codigo}
                        className="px-2 py-2 text-left font-bold border border-schmersal-blue-dark min-w-[180px]"
                        title={descricaoItemVerificacao(t, c.codigo, c.descricao)}
                      >
                        <div className="font-mono">{c.codigo}</div>
                        <div className="font-normal text-[10px] leading-tight whitespace-normal max-w-[220px]">
                          {descricaoItemVerificacao(t, c.codigo, c.descricao)}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-schmersal-gray-darker">
                  {linhas.map((l, idx) => (
                    <tr
                      key={l.apreciacaoId}
                      className={idx % 2 === 0 ? '' : 'bg-schmersal-gray-light/30 dark:bg-schmersal-gray-dark/30'}
                    >
                      <td className="px-2 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark font-semibold text-schmersal-gray-dark dark:text-white sticky left-0 bg-inherit z-10">
                        {l.nomeMaquina}
                        {l.identificacao && (
                          <div className="text-[10px] text-schmersal-gray-medium font-normal">
                            {l.identificacao}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark text-schmersal-gray-dark dark:text-white">
                        {l.setor}
                      </td>
                      <td className="px-2 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark text-schmersal-gray-dark dark:text-white">
                        <div className="font-mono text-[10px]">{l.numero}</div>
                        <div className="text-[10px] text-schmersal-gray-medium">{l.titulo}</div>
                      </td>
                      {colunas.map((c) => {
                        const it = l.itens[c.codigo];
                        const possui = it?.possui;
                        const cor =
                          possui === true
                            ? 'bg-green-50 dark:bg-green-900/20'
                            : possui === false
                              ? 'bg-red-50 dark:bg-red-900/20'
                              : 'bg-gray-50 dark:bg-schmersal-gray-darkest';
                        return (
                          <td
                            key={c.codigo}
                            className={`px-2 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark align-top ${cor}`}
                          >
                            <div className="flex items-center gap-1 font-semibold">
                              {possui === true && (
                                <CheckCircle2 className="h-3.5 w-3.5 text-green-700 dark:text-green-400" />
                              )}
                              {possui === false && (
                                <XCircle className="h-3.5 w-3.5 text-red-700 dark:text-red-400" />
                              )}
                              {(possui === null || possui === undefined) && (
                                <MinusCircle className="h-3.5 w-3.5 text-gray-500" />
                              )}
                              <span
                                className={
                                  possui === true
                                    ? 'text-green-800 dark:text-green-300'
                                    : possui === false
                                      ? 'text-red-800 dark:text-red-300'
                                      : 'text-gray-600 dark:text-gray-400'
                                }
                              >
                                {rotuloPossui(t, possui)}
                              </span>
                            </div>
                            {it?.observacao && (
                              <div className="text-[10px] text-schmersal-gray-dark dark:text-schmersal-gray-light mt-1 whitespace-pre-wrap">
                                {it.observacao}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  <tr className="bg-schmersal-blue/10 dark:bg-schmersal-blue/20 font-semibold">
                    <td
                      colSpan={3}
                      className="px-2 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark text-schmersal-blue dark:text-white sticky left-0 bg-schmersal-blue/10 dark:bg-schmersal-blue/20 z-10"
                    >
                      {t('apreciacaoTabelas.analise.totais')}
                    </td>
                    {colunas.map((c) => {
                      const s = estatisticas[c.codigo];
                      return (
                        <td
                          key={c.codigo}
                          className="px-2 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark text-[11px]"
                        >
                          <div className="text-green-700 dark:text-green-400">
                            {t('apreciacaoTabelas.analise.totalPossui', { qtd: s.possui })}
                          </div>
                          <div className="text-red-700 dark:text-red-400">
                            {t('apreciacaoTabelas.analise.totalNao', { qtd: s.naoPossui })}
                          </div>
                          <div className="text-gray-600 dark:text-gray-400">
                            {t('apreciacaoTabelas.analise.totalSemInfo', { qtd: s.semInfo })}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
